import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { rem } from 'polished'
import { useDrawerAskAI } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useDrawerAskAI'
import { useAskAIContextChip } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useAskAIContextChip'
import { useDrawerAskAIChatState } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/DrawerAskAIContext'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import ChatInputContextBar from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ChatInputContextBar'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import DrawerAskAIInput, { type DrawerAskAIInputHandle } from './DrawerAskAIInput'
import DrawerAskAIBubble from './DrawerAskAIBubble'
import { drawerBodyInlinePaddingStyles } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/drawerBodyLayout.styles'

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding-block-start: ${rem(12)};
  padding-block-end: calc(${rem(16)} + env(safe-area-inset-bottom, 0px));
  ${drawerBodyInlinePaddingStyles}
  gap: ${rem(8)};
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
  max-width: ${rem(652)};
  min-width: 0;
`

const ComposerStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${rem(652)};
  min-width: 0;

  &.has-context-bar {
    gap: 0;
  }
`

interface Props {
  contextLabel: string
  isOpen: boolean
  /** Formatted drawer details to include as context when asking from a drawer. */
  contextDetails?: string
  placeholder?: string
  /** Optional override; defaults to focusing the full chat surface. */
  onOpenFullChat?: () => void
  dismissOnBackgroundClick?: boolean
}

function DrawerAskAIFooter({
  contextLabel,
  isOpen,
  contextDetails,
  placeholder,
  onOpenFullChat,
  dismissOnBackgroundClick = false,
}: Props) {
  const intl = useIntl()
  const progress = useChatStreamStatus()
  const eventBus = useTripPlannerEventBus()
  const inputRef = useRef<DrawerAskAIInputHandle>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const { submitMessage, messages, isStreaming } = useDrawerAskAIChatState()

  const {
    handleSubmit: submitWithDrawerContext,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession,
  } = useDrawerAskAI({
    submitMessage,
    messages,
    isStreaming,
    contextLabel,
    isOpen,
    contextDetails,
  })

  const defaultPlaceholder = placeholder ?? intl.formatMessage(
    {
      id: 'johNJoOJenxVY0dTszC9',
      defaultMessage: 'Ask me anything about {contextLabel}',
      description: 'Placeholder text for AI assistant input field with context about current item',
    },
    { contextLabel },
  )

  const handleOpenFullChat = useCallback(() => {
    if (onOpenFullChat) {
      onOpenFullChat()
      return
    }
    eventBus.dispatch({ type: 'focus_chat' })
  }, [eventBus, onOpenFullChat])

  const focusInput = useCallback(() => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [])

  const { askAIContext, clearAskAIContext } = useAskAIContextChip({
    enabled: isOpen,
    onActivate: focusInput,
  })

  const handleSubmit = useCallback(async(message: string) => {
    if (askAIContext?.message) {
      const messageWithContext = appendContextTag(
        message,
        askAIContext.message,
        askAIContext.label,
      )
      clearAskAIContext()
      await submitWithDrawerContext(messageWithContext)
      return
    }
    await submitWithDrawerContext(message)
  }, [askAIContext, clearAskAIContext, submitWithDrawerContext])

  useEventSubscription('focus_floating_ask_ai', () => {
    if (!isOpen) return
    focusInput()
  })

  return (
    <FooterContainer ref={footerRef} data-testid="drawer-ask-ai-footer">
      {hasActiveSession && (
        <ContentWrapper>
          <DrawerAskAIBubble
            progress={progress}
            latestResponse={latestResponse}
            sessionHistory={sessionHistory}
            isResponseStreaming={isResponseStreaming}
            isWaitingForResponse={isWaitingForResponse}
            onOpenFullChat={handleOpenFullChat}
            onSendMessage={handleSubmit}
            dismissOnBackgroundClick={dismissOnBackgroundClick}
            clickAwayBoundaryRef={footerRef}
          />
        </ContentWrapper>
      )}
      <ComposerStack className={askAIContext ? 'has-context-bar' : undefined}>
        {askAIContext && (
          <ChatInputContextBar
            label={askAIContext.label}
            onClose={clearAskAIContext}
          />
        )}
        <DrawerAskAIInput
          ref={inputRef}
          onSubmit={handleSubmit}
          disabled={isStreaming}
          placeholder={askAIContext?.placeholder ?? defaultPlaceholder}
          hasActiveSession={hasActiveSession || !!askAIContext}
          forceExpanded
        />
      </ComposerStack>
    </FooterContainer>
  )
}

DrawerAskAIFooter.displayName = 'DrawerAskAIFooter'

export default DrawerAskAIFooter
